/* DocSummary.jsx — shared inline-bar + drawer + PDF/Excel exports for any list page.

   Props:
     kind        — backend kind key: "quotations" | "invoices" | "purchase_orders" | ... etc
     label       — display label, e.g. "Quotations"
     filters     — { status, from, to, q } current-page filters (object)
     trigger     — optional ReactNode rendered as the inline bar's right-side CTA
*/
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Badge } from './ui/badge';
import {
  ChartBar, Eye, FilePdf, FileXls, X, ArrowsCounterClockwise, CurrencyDollar,
  CheckCircle, Clock, Warning,
} from '@phosphor-icons/react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (v) =>
  (Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DocSummary({ kind, label, filters }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drawer, setDrawer] = useState(false);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    if (filters?.status && filters.status !== 'all') p.set('status', filters.status);
    if (filters?.from) p.set('from', filters.from);
    if (filters?.to) p.set('to', filters.to);
    if (filters?.q) p.set('q', filters.q);
    return p;
  }, [filters]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/summary/${kind}?${buildParams().toString()}`);
      setStats(data);
    } catch (e) {
      toast.error(`Failed to load ${label} summary`);
    } finally {
      setLoading(false);
    }
  }, [kind, label, buildParams]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const download = async (format) => {
    try {
      const res = await axios.get(
        `${API_URL}/summary/${kind}/${format}?${buildParams().toString()}`,
        { responseType: 'blob' },
      );
      const ext = format === 'excel' ? 'xlsx' : format;
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${label.replace(/\s+/g, '_')}_Summary.${ext}`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Downloaded ${ext.toUpperCase()}`);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const viewPdf = async () => {
    try {
      const token = localStorage.getItem('erp_token');
      const res = await axios.get(
        `${API_URL}/summary/${kind}/pdf?${buildParams().toString()}&inline=true`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      window.open(window.URL.createObjectURL(blob), '_blank');
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  const kpis = useMemo(() => {
    if (!stats) return [];
    const out = [
      { label: 'Count', value: stats.count, icon: ChartBar, color: 'text-blue-600 dark:text-blue-400' },
    ];
    if (stats.amount_aware && stats.kind === 'invoices') {
      out.push(
        { label: 'Approved Value (incl. VAT)', value: fmtMoney(stats.total_value), icon: CurrencyDollar, color: 'text-emerald-700 dark:text-emerald-400', money: true },
        { label: 'Before VAT', value: fmtMoney(stats.total_value_excl_vat), icon: CurrencyDollar, color: 'text-emerald-700 dark:text-emerald-400', money: true },
        { label: 'Received', value: fmtMoney(stats.paid_amount), icon: CheckCircle, color: 'text-emerald-700 dark:text-emerald-400', money: true },
        { label: 'Outstanding', value: fmtMoney(stats.outstanding), icon: Warning, color: 'text-rose-700 dark:text-rose-400', money: true },
        { label: 'Pending Approval', value: fmtMoney(stats.pending_value), icon: Clock, color: 'text-orange-700 dark:text-orange-400', money: true },
      );
    } else if (stats.amount_aware) {
      out.push(
        { label: 'Total Value', value: fmtMoney(stats.total_value), icon: CurrencyDollar, color: 'text-emerald-700 dark:text-emerald-400', money: true },
        { label: 'Outstanding', value: fmtMoney(stats.outstanding), icon: Warning, color: 'text-rose-700 dark:text-rose-400', money: true },
      );
    }
    out.push(
      { label: 'This Month', value: stats.this_month_count, icon: Clock, color: 'text-amber-700 dark:text-amber-400' },
      { label: 'Approved', value: stats.approved_count, icon: CheckCircle, color: 'text-emerald-700 dark:text-emerald-400' },
      { label: 'Pending', value: stats.pending_count, icon: Clock, color: 'text-orange-700 dark:text-orange-400' },
    );
    return out;
  }, [stats]);

  return (
    <>
      {/* Compact stats bar */}
      <div className="rounded-lg border bg-card/60 dark:bg-card/40 p-2 mb-3 flex flex-wrap items-center gap-x-4 gap-y-2"
           data-testid={`summary-bar-${kind}`}>
        {loading && !stats ? (
          <div className="text-xs text-muted-foreground px-2 py-1">Loading {label} summary…</div>
        ) : stats ? (
          <>
            {kpis.map((k) => (
              <div key={k.label} className="flex items-center gap-2 px-2 py-1">
                <k.icon size={14} className={k.color} />
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.label}</span>
                <span className={`text-sm font-mono font-semibold ${k.money ? 'text-foreground' : ''}`} data-testid={`summary-${kind}-${k.label.toLowerCase().replace(/\s+/g, '-')}`}>{k.value}</span>
              </div>
            ))}
          </>
        ) : null}

        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={fetchStats} title="Refresh" data-testid={`summary-${kind}-refresh`}>
            <ArrowsCounterClockwise size={14} />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setDrawer(true)} data-testid={`summary-${kind}-open`}>
            <ChartBar size={14} className="mr-1" /> Summary
          </Button>
          <Button size="sm" variant="ghost" onClick={viewPdf} title="View Summary PDF" data-testid={`summary-${kind}-view`}>
            <Eye size={14} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => download('pdf')} title="Download PDF" data-testid={`summary-${kind}-pdf`}>
            <FilePdf size={14} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => download('excel')} title="Download Excel" data-testid={`summary-${kind}-excel`}>
            <FileXls size={14} />
          </Button>
        </div>
      </div>

      {/* Drawer with deeper drill-down */}
      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="right" className="sm:max-w-lg w-full overflow-y-auto">
          <SheetHeader className="flex flex-row items-center justify-between border-b pb-3 mb-3">
            <SheetTitle className="flex items-center gap-2">
              <ChartBar size={20} className="text-primary" /> {label} Summary
            </SheetTitle>
            <button onClick={() => setDrawer(false)} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
          </SheetHeader>

          {!stats ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-2">
                {kpis.map((k) => (
                  <div key={k.label} className="rounded-md border bg-muted/30 p-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.label}</div>
                    <div className="text-lg font-mono font-semibold mt-1">{k.value}</div>
                  </div>
                ))}
              </div>

              {Object.keys(stats.status_counts || {}).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">By Status</div>
                  <div className="space-y-1.5">
                    {Object.entries(stats.status_counts).map(([s, c]) => (
                      <div key={s} className="flex items-center justify-between text-sm rounded-md bg-muted/30 px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] capitalize">{s.replace(/_/g, ' ')}</Badge>
                          <span className="text-xs text-muted-foreground">{c} doc{c === 1 ? '' : 's'}</span>
                        </div>
                        {stats.amount_aware && (
                          <span className="font-mono text-xs">{fmtMoney(stats.status_amounts?.[s] || 0)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {Object.keys(stats.payment_methods || {}).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">By Payment Method</div>
                  <div className="space-y-1.5">
                    {Object.entries(stats.payment_methods).map(([m, v]) => (
                      <div key={m} className="flex items-center justify-between text-sm rounded-md bg-muted/30 px-3 py-2">
                        <span>{m}</span>
                        <span className="font-mono text-xs">{fmtMoney(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(stats.top_counterparties || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">Top Counterparties</div>
                  <div className="space-y-1">
                    {stats.top_counterparties.map((r, idx) => (
                      <div key={r.name + idx} className="flex items-center justify-between text-sm rounded-md bg-muted/20 px-3 py-2">
                        <span className="truncate max-w-[200px]">{r.name}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground">{r.count}</span>
                          <span className="font-mono text-xs font-semibold">{fmtMoney(r.value)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stats.kind === 'inventory_items' && (
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-md border bg-muted/30 p-3"><div className="text-[10px] uppercase text-muted-foreground">Stock Value</div><div className="text-lg font-mono font-semibold mt-1">{fmtMoney(stats.stock_value)}</div></div>
                  <div className="rounded-md border bg-muted/30 p-3"><div className="text-[10px] uppercase text-muted-foreground">Low Stock</div><div className="text-lg font-mono font-semibold mt-1 text-amber-600">{stats.low_stock_count}</div></div>
                  <div className="rounded-md border bg-muted/30 p-3"><div className="text-[10px] uppercase text-muted-foreground">Out of Stock</div><div className="text-lg font-mono font-semibold mt-1 text-rose-600">{stats.out_of_stock_count}</div></div>
                </div>
              )}

              <div className="pt-3 border-t flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={viewPdf}><Eye size={14} className="mr-1" /> View PDF</Button>
                <Button size="sm" variant="outline" onClick={() => download('pdf')}><FilePdf size={14} className="mr-1" /> Download PDF</Button>
                <Button size="sm" variant="outline" onClick={() => download('excel')}><FileXls size={14} className="mr-1" /> Download Excel</Button>
              </div>
              <p className="text-[10px] text-muted-foreground pt-2">
                Showing aggregate of the currently-filtered list ({stats.count} documents). Generated {stats.generated_at?.slice(0, 19).replace('T', ' ')}.
              </p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
